import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { RUN_STATUSES } from "../../features/agents/agentTaskTypes";
import {
  buildRunStorySteps,
  effectiveRunStatus,
  runKindLabel,
  runStatusChipProps,
  runStatusHeadline,
  runStatusPlainEnglish,
  runStoryCounts,
  actionIdFromRunStep,
  matchAgentActionForRunStep,
  runTimelineRecap,
  timelineOutcomeForDisplay,
} from "../../features/agents/agentRunFormat";
import { generateAgentRunRecap } from "../../features/agents/agentApi";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    root: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      background: theme.palette.background.paper,
    },
    header: {
      padding: theme.spacing(2),
      flexShrink: 0,
    },
    liveBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      marginTop: theme.spacing(1),
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color: dark ? "#a5d6a7" : "#1b5e20",
      background: dark ? "rgba(76,175,80,0.15)" : "rgba(232,245,233,0.95)",
      border: `1px solid ${dark ? "rgba(76,175,80,0.4)" : "rgba(46,125,50,0.35)"}`,
    },
    liveDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: "#2e7d32",
      animation: "$livePulse 1.4s ease-in-out infinite",
    },
    "@keyframes livePulse": {
      "0%": { transform: "scale(0.85)", opacity: 0.55 },
      "50%": { transform: "scale(1.15)", opacity: 1 },
      "100%": { transform: "scale(0.85)", opacity: 0.55 },
    },
    scrollWrap: {
      flex: 1,
      minHeight: 0,
      position: "relative",
      display: "flex",
      flexDirection: "column",
    },
    scroll: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      padding: theme.spacing(0, 2, 3),
    },
    newActivityChip: {
      position: "absolute",
      bottom: theme.spacing(2),
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 3,
      fontWeight: 700,
      boxShadow: dark
        ? "0 4px 16px rgba(0,0,0,0.45)"
        : "0 4px 16px rgba(0,0,0,0.18)",
    },
    hero: {
      borderRadius: 14,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2.5),
      background: dark ? "rgba(255,255,255,0.04)" : "#f7f8fa",
      border: `1px solid ${theme.palette.divider}`,
    },
    heroOk: {
      borderColor: dark ? "rgba(76,175,80,0.45)" : "rgba(46,125,50,0.35)",
      background: dark ? "rgba(76,175,80,0.08)" : "rgba(232,245,233,0.85)",
    },
    heroWarn: {
      borderColor: "rgba(237,108,2,0.45)",
      background: dark ? "rgba(237,108,2,0.1)" : "rgba(255,243,224,0.9)",
    },
    heroErr: {
      borderColor: dark ? "rgba(244,67,54,0.5)" : "rgba(198,40,40,0.35)",
      background: dark ? "rgba(244,67,54,0.1)" : "rgba(255,235,238,0.9)",
    },
    heroSkip: {
      borderColor: "rgba(84,110,122,0.45)",
      background: dark ? "rgba(84,110,122,0.12)" : "rgba(236,239,241,0.95)",
    },
    heroHalt: {
      borderColor: "rgba(106,27,154,0.5)",
      background: dark ? "rgba(106,27,154,0.14)" : "rgba(243,229,245,0.95)",
    },
    statRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginTop: theme.spacing(1.5),
    },
    statPill: {
      fontSize: 12,
      fontWeight: 600,
      padding: "4px 10px",
      borderRadius: 999,
      background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
      color: theme.palette.text.secondary,
    },
    sectionLabel: {
      fontWeight: 700,
      letterSpacing: 0.2,
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(0.5),
    },
    story: {
      position: "relative",
      paddingLeft: theme.spacing(0.5),
    },
    storyItem: {
      display: "flex",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
    rail: {
      width: 28,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    railDot: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(255,255,255,0.08)" : "#fff",
      border: `2px solid ${theme.palette.divider}`,
      zIndex: 1,
    },
    railDotOk: {
      borderColor: "#2e7d32",
      color: "#2e7d32",
      background: dark ? "rgba(46,125,50,0.15)" : "#e8f5e9",
    },
    railDotErr: {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main,
      background: dark ? "rgba(244,67,54,0.12)" : "#ffebee",
    },
    railDotPlan: {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      background: dark ? "rgba(25,118,210,0.12)" : "#e3f2fd",
    },
    railLine: {
      width: 2,
      flex: 1,
      minHeight: 12,
      background: theme.palette.divider,
      marginTop: 2,
    },
    card: {
      flex: 1,
      minWidth: 0,
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.paper,
      overflow: "hidden",
    },
    cardHead: {
      padding: theme.spacing(1.25, 1.5),
      cursor: "pointer",
      userSelect: "none",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      },
    },
    cardBody: {
      padding: theme.spacing(0, 1.5, 1.5),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    kvRow: {
      display: "grid",
      gridTemplateColumns: "minmax(88px, 34%) 1fr",
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 0),
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
      "&:last-child": { borderBottom: "none" },
    },
    techToggle: {
      marginTop: theme.spacing(1),
      textTransform: "none",
      fontWeight: 600,
    },
    techBox: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1),
      borderRadius: 8,
      background: dark ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.03)",
      border: `1px dashed ${theme.palette.divider}`,
      maxHeight: 200,
      overflow: "auto",
    },
    techPre: {
      margin: 0,
      fontSize: 11,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      color: theme.palette.text.secondary,
    },
    skippedBox: {
      borderRadius: 12,
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5),
      marginTop: theme.spacing(1),
      background: dark ? "rgba(255,255,255,0.03)" : "#fafafa",
    },
    recapBox: {
      marginTop: theme.spacing(3),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  };
});

const _isTableLine = (line) => line.includes("|") && line.split("|").filter((x) => x.trim()).length >= 2;
const _isSeparatorLine = (line) => {
  const t = String(line || "").trim();
  if (!t || !t.includes("|")) return false;
  return t
    .split("|")
    .map((c) => c.trim())
    .filter(Boolean)
    .every((cell) => /^:?-{3,}:?$/.test(cell));
};

const RenderRichText = ({ text }) => {
  const raw = String(text || "").replace(/\r\n/g, "\n");
  if (!raw.trim()) return null;
  const lines = raw.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] || "";
    if (line.trim().startsWith("```")) {
      i += 1;
      const codeLines = [];
      while (i < lines.length && !(lines[i] || "").trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i += 1;
      }
      if (i < lines.length && (lines[i] || "").trim().startsWith("```")) i += 1;
      blocks.push({ type: "code", text: codeLines.join("\n") });
      continue;
    }
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      blocks.push({ type: "heading", level: h[1].length, text: h[2] });
      i += 1;
      continue;
    }
    if (_isTableLine(line) && i + 1 < lines.length && _isSeparatorLine(lines[i + 1] || "")) {
      const header = line.split("|").map((c) => c.trim()).filter(Boolean);
      i += 2;
      const rows = [];
      while (i < lines.length && _isTableLine(lines[i] || "")) {
        const row = (lines[i] || "").split("|").map((c) => c.trim()).filter(Boolean);
        if (row.length) rows.push(row);
        i += 1;
      }
      blocks.push({ type: "table", header, rows });
      continue;
    }
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const num = line.match(/^\s*\d+\.\s+(.*)$/);
    if (bullet || num) {
      const items = [];
      while (i < lines.length) {
        const l = lines[i] || "";
        const b = l.match(/^\s*[-*]\s+(.*)$/);
        const n = l.match(/^\s*\d+\.\s+(.*)$/);
        if (!b && !n) break;
        items.push((b ? b[1] : n[1]) || "");
        i += 1;
      }
      blocks.push({ type: "list", ordered: Boolean(num), items });
      continue;
    }
    const para = [];
    while (i < lines.length && String(lines[i] || "").trim() !== "") {
      para.push(lines[i]);
      i += 1;
    }
    if (para.length) blocks.push({ type: "paragraph", text: para.join("\n") });
    i += 1;
  }

  return (
    <Box>
      {blocks.map((b, idx) => {
        if (b.type === "heading") {
          return (
            <Typography key={idx} variant="subtitle2" style={{ fontWeight: 700, marginTop: idx ? 10 : 0 }}>
              {b.text}
            </Typography>
          );
        }
        if (b.type === "code") {
          return (
            <Box
              key={idx}
              component="pre"
              style={{
                margin: "8px 0 0 0",
                padding: 10,
                borderRadius: 10,
                fontSize: 11,
                overflow: "auto",
                background: "rgba(0,0,0,0.04)",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              {b.text}
            </Box>
          );
        }
        if (b.type === "list") {
          return (
            <List dense key={idx} style={{ marginTop: 4 }}>
              {b.items.map((it, j) => (
                <ListItem key={j} style={{ paddingTop: 2, paddingBottom: 2 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                        {b.ordered ? `${j + 1}. ${it}` : `• ${it}`}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          );
        }
        if (b.type === "table") {
          return (
            <Box key={idx} mt={1} style={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {b.header.map((hcell, j) => (
                      <TableCell key={j} style={{ fontWeight: 700 }}>
                        {hcell}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {b.rows.map((row, rIdx) => (
                    <TableRow key={rIdx}>
                      {b.header.map((_, cIdx) => (
                        <TableCell key={cIdx}>
                          <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                            {row[cIdx] || ""}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          );
        }
        return (
          <Typography key={idx} variant="body2" color="textSecondary" component="div" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
            {b.text}
          </Typography>
        );
      })}
    </Box>
  );
};

const formatWhen = (at) => {
  if (!at) return "";
  try {
    return new Date(at).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const StoryCard = ({ item, isLast, classes }) => {
  const [open, setOpen] = useState(!item.ok); // auto-expand failures
  const [showTech, setShowTech] = useState(false);
  const isPlan = item.kind === "planning";
  const dotClass = isPlan
    ? classes.railDotPlan
    : item.ok
      ? classes.railDotOk
      : classes.railDotErr;

  let techJson = null;
  if (showTech && item.step) {
    try {
      techJson = JSON.stringify(
        {
          what_was_sent: item.detailLines?.length
            ? Object.fromEntries(item.detailLines.map((r) => [r.label, r.value]))
            : undefined,
          result: timelineOutcomeForDisplay(item.step),
        },
        null,
        2,
      );
    } catch {
      techJson = null;
    }
  }

  return (
    <Box className={classes.storyItem}>
      <Box className={classes.rail}>
        <Box className={`${classes.railDot} ${dotClass}`}>
          {isPlan ? (
            <HelpOutlineIcon style={{ fontSize: 16 }} />
          ) : item.ok ? (
            <CheckCircleOutlineIcon style={{ fontSize: 16 }} />
          ) : (
            <ErrorOutlineIcon style={{ fontSize: 16 }} />
          )}
        </Box>
        {!isLast ? <Box className={classes.railLine} /> : null}
      </Box>
      <Box className={classes.card}>
        <Box className={classes.cardHead} onClick={() => setOpen((v) => !v)}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 8 }}>
            <Box minWidth={0} flex={1}>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6 }} mb={0.25}>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                  {isPlan ? "Planning" : `Step ${item.actionNum}`}
                </Typography>
                {item.at ? (
                  <Typography variant="caption" color="textSecondary">
                    · {formatWhen(item.at)}
                  </Typography>
                ) : null}
                {!isPlan ? (
                  <Chip
                    size="small"
                    label={item.ok ? "Worked" : "Had a problem"}
                    color={item.ok ? "primary" : "secondary"}
                    style={{ height: 20, fontSize: 11 }}
                  />
                ) : null}
              </Box>
              <Typography variant="body2" style={{ fontWeight: 700, lineHeight: 1.35 }}>
                {item.title}
              </Typography>
              {item.subtitle ? (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: 4, lineHeight: 1.45, whiteSpace: "pre-wrap" }}
                >
                  {open && item.fullText && item.fullText !== item.subtitle ? item.fullText : item.subtitle}
                </Typography>
              ) : null}
            </Box>
            <Box flexShrink={0} color="text.secondary" pt={0.25}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>
          </Box>
        </Box>
        <Collapse in={open}>
          <Box className={classes.cardBody}>
            {!isPlan && item.errorDetail ? (
              <Typography variant="body2" color="error" style={{ marginBottom: 8, whiteSpace: "pre-wrap" }}>
                {item.errorDetail}
              </Typography>
            ) : null}
            {!isPlan && item.detailLines?.length ? (
              <Box mt={0.5}>
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 700 }}>
                  What it used
                </Typography>
                {item.detailLines.map((row, i) => (
                  <Box key={i} className={classes.kvRow}>
                    <Typography variant="caption" color="textSecondary">
                      {row.label}
                    </Typography>
                    <Typography variant="body2" style={{ wordBreak: "break-word" }}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
            {!isPlan ? (
              <>
                <Button
                  size="small"
                  color="default"
                  className={classes.techToggle}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTech((v) => !v);
                  }}
                >
                  {showTech ? "Hide technical details" : "Show technical details"}
                </Button>
                <Collapse in={showTech}>
                  <Box className={classes.techBox}>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      For support / debugging — most people can ignore this.
                    </Typography>
                    <pre className={classes.techPre}>{techJson || "No extra details stored."}</pre>
                  </Box>
                </Collapse>
              </>
            ) : null}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

const AgentTaskDetailPane = ({
  run,
  loading,
  onClose,
  agentActions = [],
  agentId = null,
  liveUpdating = false,
  livePulseAt = null,
  onRecapGenerated = null,
}) => {
  const classes = useStyles();
  const [recapBusy, setRecapBusy] = useState(false);
  const [recapErr, setRecapErr] = useState(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const [flashNew, setFlashNew] = useState(false);
  const [hasNewActivity, setHasNewActivity] = useState(false);
  const scrollRef = React.useRef(null);
  const prevStepCountRef = React.useRef(0);
  const stickToBottomRef = React.useRef(true);
  const runKey = run?.run_id || run?.task_id || null;

  const story = useMemo(() => buildRunStorySteps(run, agentActions), [run, agentActions]);
  const counts = useMemo(() => runStoryCounts(story), [story]);

  const NEAR_BOTTOM_PX = 80;

  const isNearBottom = (el) => {
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  };

  const scrollToLatest = () => {
    stickToBottomRef.current = true;
    setHasNewActivity(false);
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = isNearBottom(el);
    stickToBottomRef.current = nearBottom;
    if (nearBottom) setHasNewActivity(false);
  };

  useEffect(() => {
    stickToBottomRef.current = true;
    setHasNewActivity(false);
    prevStepCountRef.current = 0;
  }, [runKey]);

  useEffect(() => {
    if (!liveUpdating) setHasNewActivity(false);
  }, [liveUpdating]);

  useEffect(() => {
    if (!livePulseAt) return undefined;
    setFlashNew(true);
    const t = setTimeout(() => setFlashNew(false), 900);
    return () => clearTimeout(t);
  }, [livePulseAt]);

  // While live and pinned to bottom, keep the latest step in view.
  // If the user scrolled up, show a chip instead of forcing the scroll.
  useEffect(() => {
    const n = story.length;
    if (liveUpdating && n > prevStepCountRef.current) {
      if (stickToBottomRef.current && scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        });
        setHasNewActivity(false);
      } else if (!stickToBottomRef.current) {
        setHasNewActivity(true);
      }
    }
    prevStepCountRef.current = n;
  }, [story.length, liveUpdating]);

  if (loading && !run) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (!run) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">Select a run to see what happened.</Typography>
      </Box>
    );
  }

  const displayStatus = effectiveRunStatus(run);
  const headline = runStatusHeadline(run);
  const plain = runStatusPlainEnglish(run);
  const recapText = runTimelineRecap(run, agentActions);

  const heroClass =
    displayStatus === RUN_STATUSES.SUCCESS
      ? classes.heroOk
      : displayStatus === RUN_STATUSES.PARTIAL_SUCCESS
        ? classes.heroWarn
        : displayStatus === RUN_STATUSES.ERROR
          ? classes.heroErr
          : displayStatus === RUN_STATUSES.SKIPPED
            ? classes.heroSkip
            : displayStatus === RUN_STATUSES.HALTED
              ? classes.heroHalt
              : "";

  const HeroIcon =
    displayStatus === RUN_STATUSES.SUCCESS
      ? CheckCircleOutlineIcon
      : displayStatus === RUN_STATUSES.ERROR || displayStatus === RUN_STATUSES.PARTIAL_SUCCESS
        ? ErrorOutlineIcon
        : displayStatus === RUN_STATUSES.SKIPPED
          ? PauseCircleOutlineIcon
          : displayStatus === RUN_STATUSES.HALTED
            ? ReportProblemOutlinedIcon
            : HelpOutlineIcon;

  const canRequestAiRecap =
    Boolean(agentId) &&
    [
      RUN_STATUSES.SUCCESS,
      RUN_STATUSES.PARTIAL_SUCCESS,
      RUN_STATUSES.SKIPPED,
      RUN_STATUSES.HALTED,
      RUN_STATUSES.ERROR,
      RUN_STATUSES.CANCELLED,
    ].includes(displayStatus) &&
    (run.steps || []).length > 0;

  const handleAiRecap = async () => {
    if (!agentId || !run) return;
    setRecapErr(null);
    setRecapBusy(true);
    try {
      const rid = run.run_id || run.task_id;
      if (rid == null || String(rid).trim() === "") throw new Error("Missing run id");
      const { recap } = await generateAgentRunRecap(agentId, String(rid), { force: Boolean(run.recap) });
      if (recap && typeof onRecapGenerated === "function") onRecapGenerated(recap);
    } catch (e) {
      setRecapErr(e?.message || "Could not generate summary");
    } finally {
      setRecapBusy(false);
    }
  };

  const notRunTools = (() => {
    const actions = Array.isArray(agentActions) ? agentActions : [];
    if (!actions.length) return [];
    const toolSteps = (run.steps || []).filter(
      (s) => s && (s.phase === "tool_call" || s.phase === "error"),
    );
    const matched = new Set();
    for (const step of toolSteps) {
      const hit = matchAgentActionForRunStep(step, actions);
      if (hit?.action_id) {
        matched.add(hit.action_id);
        continue;
      }
      // Still credit raw receipt action_id even if agent definition is stale/missing.
      const stepAid = actionIdFromRunStep(step);
      if (stepAid) matched.add(stepAid);
    }
    return actions.filter((a) => a?.action_id && !matched.has(a.action_id));
  })();

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box minWidth={0} pr={1}>
            <Typography variant="h6" style={{ lineHeight: 1.25, fontWeight: 700 }}>
              What happened
            </Typography>
            <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 2 }}>
              {runKindLabel(run)}
              {run.created_at || run.started_at || run.at
                ? ` · ${formatWhen(run.created_at || run.started_at || run.at)}`
                : ""}
            </Typography>
            <Box mt={1} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 6 }}>
              <Chip size="small" {...runStatusChipProps(displayStatus)} />
              {liveUpdating ? (
                <span className={classes.liveBadge} aria-live="polite">
                  <span className={classes.liveDot} />
                  Live — updating as steps finish
                  {flashNew ? " · new activity" : ""}
                </span>
              ) : null}
            </Box>
          </Box>
          <IconButton aria-label="Close" size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Divider />
      <Box className={classes.scrollWrap}>
        <Box className={classes.scroll} ref={scrollRef} onScroll={handleScroll}>
          {loading && run ? (
            <Box mb={1}>
              <Typography variant="caption" color="textSecondary">
                Refreshing…
              </Typography>
            </Box>
          ) : null}
          <Box
            className={`${classes.hero} ${heroClass}`}
            style={flashNew ? { transition: "box-shadow 0.3s ease", boxShadow: "0 0 0 2px rgba(46,125,50,0.35)" } : undefined}
          >
            <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
              <HeroIcon style={{ marginTop: 2, flexShrink: 0 }} />
              <Box minWidth={0}>
                <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {liveUpdating ? "Working on it…" : headline}
                </Typography>
                <Typography variant="body2" style={{ marginTop: 6, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                  {liveUpdating
                    ? "This run is still going. New steps will appear below automatically — no need to refresh."
                    : plain}
                </Typography>
                <Box className={classes.statRow}>
                  {counts.actions > 0 ? (
                    <span className={classes.statPill}>
                      {counts.ok} of {counts.actions} action{counts.actions === 1 ? "" : "s"} worked
                    </span>
                  ) : (
                    <span className={classes.statPill}>No actions ran</span>
                  )}
                  {counts.fail > 0 ? (
                    <span className={classes.statPill}>
                      {counts.fail} problem{counts.fail === 1 ? "" : "s"}
                    </span>
                  ) : null}
                  {counts.planning > 0 ? (
                    <span className={classes.statPill}>
                      {counts.planning} planning note{counts.planning === 1 ? "" : "s"}
                    </span>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Box>

          <Typography variant="subtitle2" className={classes.sectionLabel}>
            Step by step
          </Typography>
          {story.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              We don’t have a detailed breakdown for this run yet. Newer runs will show each step in plain English here.
            </Typography>
          ) : (
            <Box className={classes.story}>
              {story.map((item, idx) => (
                <StoryCard
                  key={item.step?.step_id || `${item.kind}-${idx}`}
                  item={item}
                  isLast={idx === story.length - 1}
                  classes={classes}
                />
              ))}
            </Box>
          )}

          {notRunTools.length > 0 ? (
            <Box mt={2}>
              <Button
                size="small"
                color="default"
                className={classes.techToggle}
                startIcon={showSkipped ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowSkipped((v) => !v)}
              >
                {showSkipped
                  ? "Hide tools that didn’t run"
                  : `${notRunTools.length} configured tool${notRunTools.length === 1 ? "" : "s"} didn’t run this time`}
              </Button>
              <Collapse in={showSkipped}>
                <Box className={classes.skippedBox}>
                  <Typography variant="caption" color="textSecondary" paragraph style={{ marginBottom: 8 }}>
                    These are available on the agent but weren’t needed (or weren’t reached) for this run. That’s normal.
                  </Typography>
                  <List dense disablePadding>
                    {notRunTools.map((a) => {
                      const name =
                        a.label ||
                        `${a.app_label || a.app || "Tool"} — ${a.action_label || a.action_key || ""}`;
                      return (
                        <ListItem key={a.action_id} style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {name.trim() || a.action_id}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Collapse>
            </Box>
          ) : null}

          {(displayStatus === RUN_STATUSES.SUCCESS ||
            displayStatus === RUN_STATUSES.PARTIAL_SUCCESS ||
            displayStatus === RUN_STATUSES.SKIPPED ||
            displayStatus === RUN_STATUSES.ERROR ||
            displayStatus === RUN_STATUSES.CANCELLED) &&
          (String(recapText || "").trim() || canRequestAiRecap) ? (
            <Box className={classes.recapBox}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Plain-language summary
                </Typography>
                {canRequestAiRecap ? (
                  <Button size="small" color="primary" variant="outlined" disabled={recapBusy} onClick={handleAiRecap}>
                    {recapBusy ? "Writing…" : run?.recap || run?.run_recap ? "Rewrite summary" : "Write a summary"}
                  </Button>
                ) : null}
              </Box>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4, marginBottom: 8 }}>
                A short write-up of this run you can share with your team.
              </Typography>
              {recapErr ? (
                <Typography variant="body2" color="error" paragraph>
                  {recapErr}
                </Typography>
              ) : null}
              {String(recapText || "").trim() ? (
                <RenderRichText text={recapText} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No summary yet — tap “Write a summary” if you’d like one.
                </Typography>
              )}
            </Box>
          ) : null}

          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            style={{ marginTop: 24, opacity: 0.7 }}
          >
            Ref {run.run_id || run.task_id || "—"}
          </Typography>
        </Box>
        {hasNewActivity && liveUpdating ? (
          <Chip
            className={classes.newActivityChip}
            color="primary"
            clickable
            icon={<KeyboardArrowDownIcon />}
            label="New activity"
            onClick={scrollToLatest}
            aria-label="Scroll to new activity"
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default AgentTaskDetailPane;
